import {
  Button,
  Icon,
  LoaderBar,
  Search,
} from "@screencloud/screencloud-ui-components";
import { UUID } from "@screencloud/uuid";
import { useCallback, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import classNames from "clsx";
import { AppInstance } from "src/types.g";
import EmptyState from "src/components/EmptyState";
import SearchAppPicker from "src/components/QuickPostPicker/components/Search";
import { Styled } from "src/components/QuickPostPicker/styles";
import { useAppContext } from "src/hooks/useAppContext";
import { useStateSearch } from "src/hooks/useStateSearch";
import { useMediaPickerAppInstances } from "src/components/AppPicker/hooks/useAppInstancePicker";
import { QuickPostInstances } from "src/components/QuickPostPicker/components/QuickPostInstances";
import { useTagSelection } from "src/components/TagList/useTagSelection";
import { useAppToggleSelection } from "src/hooks/useAppToggleSelection";
import { useSearchAppInstancePickerByAppId } from "src/components/AppPicker/hooks/useSearchAppInstancePickerByAppId";
import { TagFilterDropdown } from "src/components/TagFilterDropdown";
import { useGetScreenSize } from "src/hooks/useGetScreenSize";
import { MediaPickerSelectedTagList } from "../MediaPickerSelectedTagList";
import uniqBy from "lodash/uniqBy";

interface Props {
  isMultipleSelect: boolean | undefined;
  callback: (ids: string[], selectedApps: AppInstance[]) => void;
  spaceId?: UUID;
}

const QuickPostPicker = ({ isMultipleSelect, spaceId, callback }: Props) => {
  const context = useAppContext();
  const { isTabletView } = useGetScreenSize();
  const { query, searchString, updateSearchString, clearSearch } =
    useStateSearch();

  const {
    onClickTag,
    selectedTagForQuery,
    hasSelectedTags,
    selectedTags,
    onSetelectTags,
    onClearTags,
  } = useTagSelection();

  const searchQuickPosts = useSearchAppInstancePickerByAppId(
    query,
    context.quickPostAppId,
    spaceId,
  );

  const quickPosts = useMediaPickerAppInstances({
    appId: context.quickPostAppId,
    spaceId: spaceId as UUID,
    tags: selectedTagForQuery,
  });

  const allQuickPostInstances = useMediaPickerAppInstances({
    appId: context.quickPostAppId,
    spaceId: spaceId as UUID,
  });

  const { onToggleSelection, selectedIds } = useAppToggleSelection({
    appInstances: uniqBy(
      [
        ...allQuickPostInstances.appInstances,
        ...searchQuickPosts.appInstances,
        ...quickPosts.appInstances,
      ],
      "id",
    ),
    isMultipleSelect,
    callback,
  });

  const handleSearch = useCallback(
    (_: React.SyntheticEvent, data: { value: string }) =>
      updateSearchString(data.value),
    [updateSearchString],
  );

  const renderHeader = () => (
    <div
      className={classNames("content-header", {
        "media-tags": hasSelectedTags,
      })}
    >
      <div className="search-filter-container">
        <Search
          className="search"
          onClear={clearSearch}
          onChange={handleSearch}
          placeholder="Search Quick Post"
          showNoResults={false}
          value={searchString}
        />
        <TagFilterDropdown
          className="filter-dropdown"
          selectedTags={selectedTags}
          appId={context.quickPostAppId}
          spaceId={context.user.settings.spaceId}
          onSelect={onSetelectTags}
          overlayWhenClick={isTabletView}
        />
        <Button
          onClick={onClearTags}
          className="clear-all-tags-button"
          data-testid="clear-all-tags-button"
          disabled={!hasSelectedTags}
        >
          <Icon name="close" />
          <FormattedMessage
            id="ui_component.common.label.clear_all"
            defaultMessage="Clear All"
          />
        </Button>
      </div>
      {hasSelectedTags && (
        <MediaPickerSelectedTagList
          selectedTags={selectedTags}
          onClearTags={onClearTags}
          onClickTag={onClickTag}
        />
      )}
    </div>
  );

  const content = useMemo(() => {
    if (searchString.length > 0) {
      return (
        <SearchAppPicker
          selectedIds={selectedIds}
          onToggleSelection={onToggleSelection}
          secureMediaPolicy={context.secureMediaPolicy}
          {...searchQuickPosts}
        />
      );
    }

    return (
      <QuickPostInstances
        selectedIds={selectedIds}
        onToggleSelection={onToggleSelection}
        onClickTag={onClickTag}
        {...quickPosts}
      />
    );
  }, [
    searchString,
    selectedIds,
    onToggleSelection,
    query,
    context.quickPostAppId,
    spaceId,
    isMultipleSelect,
    callback,
    quickPosts,
    searchQuickPosts,
  ]);

  return (
    <Styled className="media-content apps">
      {renderHeader()}
      <div className="container">
        <div className="layout-list" id="scrollableDiv">
          {content}
        </div>
        {quickPosts.appInstances?.length === 0 && !quickPosts.loading && (
          <EmptyState section="app-picker" cover={false} className="empty">
            <h3>
              <FormattedMessage
                id="quick_post.empty.no_content"
                defaultMessage="No Quick Post added yet"
              />
            </h3>
            <p>
              <FormattedMessage
                id="quick_post.empty.add_content"
                defaultMessage="Make a Quick Post to be able to add this content"
              />
            </p>
          </EmptyState>
        )}
      </div>
      {quickPosts.loading && <LoaderBar />}
    </Styled>
  );
};

export default QuickPostPicker;
